import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    ArrowLeft,
    Save,
    CreditCard,
    AlertCircle,
    Calendar,
    User,
    DollarSign,
    Plus
} from 'lucide-react';
import { useGetAllMembershipPlansQuery, MembershipPlan } from '../../store/slices/apiSlice';
import { toast } from 'react-hot-toast';
import { Button } from '../../components/ui/Button';

const CreateMembership: React.FC = () => {
    const navigate = useNavigate();
    const { data: plansResponse } = useGetAllMembershipPlansQuery({});
    const plans = plansResponse?.data || [];

    const [formData, setFormData] = useState({
        user_id: '',
        plan_id: '',
        start_date: '',
        end_date: '',
        payment_type: 'one_time' as 'one_time' | 'installments',
        status: 'active' as 'active' | 'expired' | 'cancelled',
        total_installments: '',
        installments_paid: '0'
    });

    const [errors, setErrors] = useState<Partial<Record<keyof typeof formData, string>>>({});

    const handleInputChange = (field: keyof typeof formData, value: any) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: undefined
            }));
        }

        // Auto-calculate end date if plan and start date are set
        if (field === 'start_date' || field === 'plan_id') {
            const selectedPlan = plans.find((p: MembershipPlan) => p.plan_id.toString() === formData.plan_id || (field === 'plan_id' && p.plan_id.toString() === value));
            const startDate = field === 'start_date' ? value : formData.start_date;
            
            if (selectedPlan && startDate && selectedPlan.duration_days) {
                const start = new Date(startDate);
                const end = new Date(start);
                end.setDate(end.getDate() + selectedPlan.duration_days);
                setFormData(prev => ({
                    ...prev,
                    end_date: end.toISOString().split('T')[0]
                }));
            }
        }
    };

    const validateForm = (): boolean => {
        const newErrors: Partial<Record<keyof typeof formData, string>> = {};

        if (!formData.user_id.trim()) {
            newErrors.user_id = 'User ID is required';
        } else if (isNaN(Number(formData.user_id)) || Number(formData.user_id) <= 0) {
            newErrors.user_id = 'User ID must be a valid number';
        }

        if (!formData.plan_id) {
            newErrors.plan_id = 'Plan is required';
        }

        if (!formData.start_date) {
            newErrors.start_date = 'Start date is required';
        }

        if (!formData.end_date) {
            newErrors.end_date = 'End date is required';
        }

        if (formData.payment_type === 'installments') {
            if (!formData.total_installments || isNaN(Number(formData.total_installments)) || Number(formData.total_installments) <= 0) {
                newErrors.total_installments = 'Total installments must be a valid number greater than 0';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        // Note: This is a placeholder - the API endpoint needs to be added to apiSlice.ts
        // For now, we'll show an error that this feature needs API support
        toast.error('Create membership API endpoint needs to be implemented');
        
        // TODO: Uncomment when API endpoint is available
        /*
        try {
            await createMembership({
                user_id: Number(formData.user_id),
                plan_id: Number(formData.plan_id),
                start_date: formData.start_date,
                end_date: formData.end_date,
                payment_type: formData.payment_type,
                status: formData.status,
                ...(formData.payment_type === 'installments' && {
                    total_installments: Number(formData.total_installments),
                    installments_paid: Number(formData.installments_paid)
                })
            }).unwrap();

            toast.success('Membership created successfully!');
            navigate('/admin/memberships');
        } catch (error: any) {
            console.error('Failed to create membership:', error);
            toast.error(error?.data?.message || 'Failed to create membership');
        }
        */
    };

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-white border-b border-gray-200">
                <div className="px-6 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <button
                                onClick={() => navigate('/admin/memberships')}
                                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <ArrowLeft className="w-5 h-5" />
                            </button>
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900">Create Membership</h1>
                                <p className="text-gray-600">Add a new membership for a user</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="px-6 py-6">
                <div className="max-w-4xl mx-auto">
                    <form onSubmit={handleSubmit} className="space-y-8">
                        {/* Basic Information */}
                        <div className="bg-white rounded-lg shadow">
                            <div className="px-6 py-4 border-b border-gray-200">
                                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                                    <CreditCard className="w-5 h-5 mr-2" />
                                    Basic Information
                                </h2>
                            </div>
                            <div className="p-6 space-y-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        User ID *
                                    </label>
                                    <div className="relative">
                                        <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                                        <input
                                            type="number"
                                            value={formData.user_id}
                                            onChange={(e) => handleInputChange('user_id', e.target.value)}
                                            className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                                errors.user_id ? 'border-red-300' : 'border-gray-300'
                                            }`}
                                            placeholder="Enter user ID"
                                        />
                                    </div>
                                    {errors.user_id && (
                                        <p className="mt-1 text-sm text-red-600 flex items-center">
                                            <AlertCircle className="w-4 h-4 mr-1" />
                                            {errors.user_id}
                                        </p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Membership Plan *
                                    </label>
                                    <select
                                        value={formData.plan_id}
                                        onChange={(e) => handleInputChange('plan_id', e.target.value)}
                                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                            errors.plan_id ? 'border-red-300' : 'border-gray-300'
                                        }`}
                                    >
                                        <option value="">Select a plan</option>
                                        {plans.map((plan: MembershipPlan) => (
                                            <option key={plan.plan_id} value={plan.plan_id}>
                                                {plan.name} - ${plan.price} ({plan.duration_days} days)
                                            </option>
                                        ))}
                                    </select>
                                    {errors.plan_id && (
                                        <p className="mt-1 text-sm text-red-600 flex items-center">
                                            <AlertCircle className="w-4 h-4 mr-1" />
                                            {errors.plan_id}
                                        </p>
                                    )}
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Start Date *
                                        </label>
                                        <div className="relative">
                                            <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                                            <input
                                                type="date"
                                                value={formData.start_date}
                                                onChange={(e) => handleInputChange('start_date', e.target.value)}
                                                className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                                    errors.start_date ? 'border-red-300' : 'border-gray-300'
                                                }`}
                                            />
                                        </div>
                                        {errors.start_date && (
                                            <p className="mt-1 text-sm text-red-600 flex items-center">
                                                <AlertCircle className="w-4 h-4 mr-1" />
                                                {errors.start_date}
                                            </p>
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            End Date *
                                        </label>
                                        <div className="relative">
                                            <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                                            <input
                                                type="date"
                                                value={formData.end_date}
                                                onChange={(e) => handleInputChange('end_date', e.target.value)}
                                                className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                                    errors.end_date ? 'border-red-300' : 'border-gray-300'
                                                }`}
                                            />
                                        </div>
                                        {errors.end_date && (
                                            <p className="mt-1 text-sm text-red-600 flex items-center">
                                                <AlertCircle className="w-4 h-4 mr-1" />
                                                {errors.end_date}
                                            </p>
                                        )}
                                        <p className="text-xs text-gray-500 mt-1">
                                            End date will be auto-calculated based on plan duration
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Payment Information */}
                        <div className="bg-white rounded-lg shadow">
                            <div className="px-6 py-4 border-b border-gray-200">
                                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                                    <DollarSign className="w-5 h-5 mr-2" />
                                    Payment Information
                                </h2>
                            </div>
                            <div className="p-6 space-y-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Payment Type *
                                    </label>
                                    <select
                                        value={formData.payment_type}
                                        onChange={(e) => handleInputChange('payment_type', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    >
                                        <option value="one_time">One Time Payment</option>
                                        <option value="installments">Installments</option>
                                    </select>
                                </div>

                                {formData.payment_type === 'installments' && (
                                    <>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Total Installments *
                                            </label>
                                            <input
                                                type="number"
                                                min="1"
                                                value={formData.total_installments}
                                                onChange={(e) => handleInputChange('total_installments', e.target.value)}
                                                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                                    errors.total_installments ? 'border-red-300' : 'border-gray-300'
                                                }`}
                                                placeholder="e.g., 12"
                                            />
                                            {errors.total_installments && (
                                                <p className="mt-1 text-sm text-red-600 flex items-center">
                                                    <AlertCircle className="w-4 h-4 mr-1" />
                                                    {errors.total_installments}
                                                </p>
                                            )}
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Installments Paid
                                            </label>
                                            <input
                                                type="number"
                                                min="0"
                                                value={formData.installments_paid}
                                                onChange={(e) => handleInputChange('installments_paid', e.target.value)}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                placeholder="0"
                                            />
                                        </div>
                                    </>
                                )}

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Status *
                                    </label>
                                    <select
                                        value={formData.status}
                                        onChange={(e) => handleInputChange('status', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    >
                                        <option value="active">Active</option>
                                        <option value="expired">Expired</option>
                                        <option value="cancelled">Cancelled</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* Information Note */}
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                            <div className="flex items-start">
                                <AlertCircle className="w-5 h-5 text-gray-600 mr-3 flex-shrink-0 mt-0.5" />
                                <div>
                                    <h4 className="text-sm font-medium text-gray-900 mb-1">Note</h4>
                                    <p className="text-xs text-gray-700">
                                        Creating a membership manually will assign the selected plan to the user. 
                                        Make sure the user ID exists and the dates are valid.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Submit Buttons */}
                        <div className="flex items-center justify-end space-x-4">
                            <Button
                                variant="outline"
                                onClick={() => navigate('/admin/memberships')}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                variant="primary"
                                disabled={!formData.user_id || !formData.plan_id || !formData.start_date || !formData.end_date}
                            >
                                <Plus className="w-4 h-4 mr-2" />
                                Create Membership
                            </Button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default CreateMembership;

